import { useRef, useState, useEffect } from 'react'
import { ScrollView, TextInput, View, Text, StyleSheet, Platform } from 'react-native'
import { BackButton } from '@/src/common/components/BackButton'
import { Routes } from '@/src/common/enums/routes'
import { Input, InputPhone } from '@/src/common/components/Input'
import { Mail } from '@/src/assets/icons/Mail'
import { Button } from '@/src/common/components/Button'
import { useFocusEffect, useRouter } from 'expo-router'
import { useLogError } from '@/src/common/hooks/useLogError'
import { useUserStore } from '@/src/common/store/useUserStore'
import { validateCardApplication } from '@/src/services/forms'
import { parsePhoneNumber } from 'libphonenumber-js'
import useBasicInfoCardApplicationStore from '@/src/common/store/useBasicInfoCardApplicationStore'
import { useSardine } from '@/src/services/Sardine'
import { mesaColorNames, typography } from '@/src/common/theme'
import { CardApplicationWrapper } from './cardApplicationWrapper'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import { GET_USER_INFO } from '@/src/common/gql/queries/user'
import * as E2E from '../../e2e'

export function GetStarted() {
  const { setBasicInformation } = useBasicInfoCardApplicationStore()
  const logError = useLogError()
  const router = useRouter()
  const lastNameInputRef = useRef<TextInput>(null)
  const { data: userInfo, loading: userInfoLoading } = useLoggedInQuery(GET_USER_INFO)

  // Local State
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { countryCode, userInformation } = useUserStore()
  const [isSubmitButtonEnabled, setIsSubmitButtonEnabled] = useState(true)

  const { submitData, updateOptions } = useSardine()

  const phoneNumberInDatabase = userInfo?.getUserInfo.phoneNumber

  const phoneNumberWithoutCountryCode =
    phoneNumberInDatabase != null
      ? parsePhoneNumber(phoneNumberInDatabase, {
          defaultCountry: 'US',
        }).formatNational()
      : phoneNumberInDatabase

  const [applicationInput, setApplicationInput] = useState({
    firstName: userInfo?.getUserInfo.firstName,
    lastName: userInfo?.getUserInfo.lastName,
    phoneNumber: phoneNumberInDatabase,
    email: userInfo?.getUserInfo.email,
  })

  const [applicationInputError, setApplicationInputError] = useState<
    ReturnType<typeof validateCardApplication>
  >({
    firstName: undefined,
    lastName: undefined,
  })

  useFocusEffect(() => {
    setIsSubmitButtonEnabled(true)
  })

  useEffect(() => {
    void updateOptions({ flow: 'application-basic-info' })
  }, [updateOptions])

  function handleSubmit() {
    void submitData()
    const cardValidation = validateCardApplication({
      firstName: applicationInput.firstName ?? '',
      lastName: applicationInput.lastName ?? '',
    })

    const newApplicationInputError = {
      firstName: cardValidation.firstName,
      lastName: cardValidation.lastName,
    } as const satisfies typeof applicationInputError

    setApplicationInputError(newApplicationInputError)

    if (Object.values(newApplicationInputError).some((error) => error)) return

    try {
      setIsSubmitButtonEnabled(false)
      // persist it to state
      if (applicationInput.firstName && applicationInput.lastName) {
        setBasicInformation({
          firstName: applicationInput.firstName,
          lastName: applicationInput.lastName,
        })
      }

      router.push({ pathname: Routes.OfferCode.route })
    } catch (error) {
      setIsSubmitButtonEnabled(true)
      logError(error)
    }
  }

  return (
    <CardApplicationWrapper>
      <ScrollView style={[Platform.OS === 'web' && { overflow: 'visible' }, { paddingBottom: 32 }]}>
        <BackButton
          href={Routes.Main.route}
          text="Back"
          testID={E2E.Config.Ids.Press.GetStartedBack}
        />
        <View style={styles.headerText}>
          <Text style={typography['heading/heading2']}>Let's get started</Text>
        </View>
        <View>
          <Text style={[typography['paragraph/md'], styles.instructionText]}>
            Confirm your information and tell us a little more about yourself
          </Text>
        </View>
        <View style={{ gap: 10 }}>
          <Input
            defaultValue={applicationInput.firstName ?? undefined}
            textContentType="name"
            autoCorrect={false}
            inputContainerStyle={{ paddingBottom: 0, marginBottom: 0 }}
            containerStyle={styles.inputStyle}
            segmentAction="User Inputs First Name"
            placeholder="First Name"
            sardineId="firstName"
            onChangeText={(text) => setApplicationInput({ ...applicationInput, firstName: text })}
            info={{ error: applicationInputError.firstName }}
            onSubmitEditing={() => lastNameInputRef.current?.focus()}
            returnKeyType="next"
            testID={E2E.Config.Ids.Input.FirstName}
          />
          <Input
            defaultValue={applicationInput.lastName ?? undefined}
            textContentType="name"
            autoCorrect={false}
            inputContainerStyle={{ paddingBottom: 0, marginBottom: 0 }}
            containerStyle={styles.inputStyle}
            segmentAction="User Inputs Last Name"
            placeholder="Last Name"
            sardineId="lastName"
            onChangeText={(text) => setApplicationInput({ ...applicationInput, lastName: text })}
            info={{ error: applicationInputError.lastName }}
            returnKeyType="done"
            testID={E2E.Config.Ids.Input.LastName}
            ref={lastNameInputRef}
          />
          <InputPhone
            disabled
            defaultValue={phoneNumberWithoutCountryCode ?? undefined}
            placeholder="Phone number"
            inputValue={{ phoneNumber: phoneNumberWithoutCountryCode ?? '', countryCode }}
            keyboardType="phone-pad"
            containerStyle={{ paddingHorizontal: 0 }}
            maxLength={14}
            readOnly
            testID={E2E.Config.Ids.Input.PhoneNumber}
            sardineId="phoneNumber"
          />
          <Input
            disabled
            defaultValue={applicationInput.email}
            autoCapitalize="none"
            autoCorrect={false}
            textContentType="emailAddress"
            leftIcon={<Mail stroke={mesaColorNames['Greyscale/400 #A4AAAC']} />}
            inputContainerStyle={{ paddingStart: 12 }}
            containerStyle={styles.inputStyle}
            segmentAction="User Inputs Email"
            placeholder="Email Address"
            value={userInformation?.email}
            readOnly
            testID={E2E.Config.Ids.Input.Email}
            sardineId="emailAddress"
          />
        </View>
      </ScrollView>
      <View style={{ marginBottom: 10 }}>
        <Button
          segmentAction="User Creates Account"
          title="Continue"
          disabled={!isSubmitButtonEnabled || userInfoLoading}
          onPress={handleSubmit}
          testID={E2E.Config.Ids.Press.GetStartedContinue}
        />
      </View>
    </CardApplicationWrapper>
  )
}

const styles = StyleSheet.create({
  headerText: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 32,
  },
  instructionText: {
    marginVertical: 16,
    marginBottom: 32,
    lineHeight: 24,
    color: mesaColorNames['Greyscale/400 #A4AAAC'],
  },
  inputStyle: {
    paddingHorizontal: 0,
  },
  error: {
    fontSize: 14,
    color: 'red',
    marginVertical: 10,
  },
})
